import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter]);

  const fetchUsers = async () => {
    try {
      let url = `/admin/users?page=${page}&limit=20`;
      if (roleFilter && roleFilter !== 'all') url += `&role=${roleFilter}`;
      const response = await api.get(url);
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}/status?is_active=${!isActive}`);
      toast.success(`Usuário ${!isActive ? 'ativado' : 'desativado'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Voce nao pode deletar o proprio usuario logado');
      return;
    }

    const label = user.profile_summary?.name || user.email;
    const confirmed = window.confirm(
      `Deletar ${label}?\n\nEsta ação remove a conta, o perfil vinculado e os dados relacionados. Não pode ser desfeita.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Usuario deletado');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao deletar usuario';
      toast.error(message);
    }
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      patient: { label: 'Paciente', className: 'bg-blue-100 text-blue-700' },
      provider: { label: 'Prestador', className: 'bg-emerald-100 text-emerald-700' },
      admin: { label: 'Admin', className: 'bg-purple-100 text-purple-700' },
    };
    const { label, className } = roleMap[role] || roleMap.patient;
    return <Badge className={className}>{label}</Badge>;
  };

  const getPlanBadge = (user) => {
    const plan = user?.profile_summary?.plan;
    if (!plan) return <span className="text-slate-400">-</span>;

    if (plan.toLowerCase() === 'premium') {
      return <Badge className="bg-amber-100 text-amber-700">Premium</Badge>;
    }

    if (plan.toLowerCase() === 'gratuito') {
      return <Badge className="bg-slate-100 text-slate-700">Gratuito</Badge>;
    }

    return <Badge className="bg-blue-100 text-blue-700">{plan}</Badge>;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Usuários</h1>
            <p className="text-slate-600 mt-1">Gerenciar contas de usuários</p>
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos os perfis" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os perfis</SelectItem>
              <SelectItem value="patient">Pacientes</SelectItem>
              <SelectItem value="provider">Prestadores</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Usuário</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Perfil</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Plano</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Especialidade</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Cidade</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Criado em</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    Carregando...
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{user.profile_summary?.name || user.email}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <p className="text-xs text-slate-500">{user.id}</p>
                    </td>
                    <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4">{user.role === 'admin' ? <span className="text-slate-400">-</span> : getPlanBadge(user)}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{user.role === 'admin' ? '-' : (user.profile_summary?.specialty || '-')}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{user.role === 'admin' ? '-' : (user.profile_summary?.city || '-')}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{formatDate(user.created_at)}</td>
                    <td className="px-6 py-4">
                      <Badge className={user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {user.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(user.id, user.is_active)}
                          className={user.is_active ? 'text-red-600' : 'text-emerald-600'}
                        >
                          {user.is_active ? (
                            <>
                              <ToggleRight className="w-4 h-4 mr-1" />
                              Desativar
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4 mr-1" />
                              Ativar
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.id === currentUser?.id}
                          className="text-red-700 hover:bg-red-50 hover:text-red-800 disabled:text-slate-300"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Deletar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-4 text-sm text-slate-600">
              Página {page} de {totalPages}
            </span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
